import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateIntegrationMutation } from "@/store/rest_api";
import { INTEGRATION_PROVIDERS, getProvider, providerInitials } from "@/util/integrationProviders";
import { IntegrationProviderKey } from "@/model/interface/IntegrationProvider";
import toast from "react-hot-toast";

export function NewIntegration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [postIntegration, postIntegrationResult] = useCreateIntegrationMutation();

  const available = INTEGRATION_PROVIDERS.filter((p) => p.available);
  const initialKey = searchParams.get("service") as IntegrationProviderKey | null;

  const [selectedKey, setSelectedKey] = useState<IntegrationProviderKey | null>(
    initialKey && getProvider(initialKey)?.available ? initialKey : null,
  );
  const [label, setLabel] = useState("");
  const [workspaceDomain, setWorkspaceDomain] = useState("");

  const selected = getProvider(selectedKey ?? undefined);

  useEffect(() => {
    if (postIntegrationResult.isSuccess) {
      navigate("/integrations");
    }
  }, [postIntegrationResult.isSuccess]);

  function handleCreate() {
    if (!selected) {
      toast.error("Select a provider to continue.");
      return;
    }
    if (label.trim().length < 2) {
      toast.error("Label must be at least 2 characters.");
      return;
    }
    if (selected.requiresDomain && workspaceDomain.trim().length < 5) {
      toast.error("Workspace domain is required.");
      return;
    }
    postIntegration({ label, service: selected.key, workspaceDomain });
  }

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Add an integration</h1>
        <p className="text-muted-foreground">Choose a provider to connect.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {available.map((provider) => {
          const isSelected = provider.key === selectedKey;
          return (
            <Card
              key={provider.key}
              onClick={() => setSelectedKey(provider.key)}
              className={`cursor-pointer transition-colors ${isSelected ? "border-primary ring-1 ring-primary" : "hover:border-muted-foreground/40"}`}
            >
              <CardContent className="flex items-center gap-3 p-4">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: provider.accent }}
                >
                  {providerInitials(provider.name)}
                </div>
                <div>
                  <p className="font-medium leading-tight">{provider.name}</p>
                  <p className="text-xs text-muted-foreground">{provider.category}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selected && (
        <div className="mt-8 max-w-lg space-y-4">
          <div className="space-y-2">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              placeholder={`${selected.name} integration`}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Your display name for this integration.</p>
          </div>

          {selected.requiresDomain && (
            <div className="space-y-2">
              <Label htmlFor="workspaceDomain">Workspace domain</Label>
              <Input
                id="workspaceDomain"
                placeholder="https://app-enterprise-0000.my.salesforce.com"
                value={workspaceDomain}
                onChange={(e) => setWorkspaceDomain(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Your {selected.name} workspace domain.</p>
            </div>
          )}

          <Button onClick={handleCreate} disabled={postIntegrationResult.isLoading}>
            {postIntegrationResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </div>
      )}
    </div>
  );
}
